import { AxiosInstance } from 'axios';
import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';
import { Discount } from 'hooks/usePropertyDiscount';
import { useCallback, useState } from 'react';

export interface Address {
  addrLine1: string;
  addrLine2: string;
  city: string;
  postcode: string;
}

export interface Facet {
  name: string;
  icon: string;
  iconAltText: string;
  isKey: boolean;
}

export interface Floorplan {
  title: string;
  description: string | null;
  link: string;
  image: string;
}

export interface Room {
  name: string;
  count: number;
}

export interface BookingSlot {
  id: number;
  name: string;
  startTime: string;
  endTime: string;
  daysOfWeek: string[];
}

const weekdays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

type OpeningTimeKey = `${(typeof weekdays)[number]}${'Start' | 'End'}`;
export type OpeningTimes = Partial<Record<OpeningTimeKey, string>>;

export interface Closure {
  startDate: string;
  endDate: string;
}

export interface Space {
  id: number;
  name: string;
  clinical: boolean;
  image: {
    regular: string;
    thumbnail: string;
  };
  images: {
    regular: string[];
    thumbnail: string[];
  };
  standardPrice: string;
  priceIncVat: string;
  capacity: number;
  openingTimes: OpeningTimes;
  description: string | null;
  floor: string;
  floorplan?: string;
  discount?: Discount;
  // The room types this space belongs to; used to match room-type discounts.
  categoryIds?: number[];
}

export interface Venue {
  nonClinicalOpeningTimes: OpeningTimes;
  id: number;
  name: string;
  telephone: string;
  contactEmail: string;
  address: Address;
  latLng?: { lat: number; lng: number };
  images: {
    regular: string[];
    thumbnail: string[];
  };
  description: string | null;
  distance: number | null;
  path: string;
  spaces?: Space[];
  clinicalRooms: Room[];
  nonClinicalRooms: Room[];
  fullAddress: string;
  bookingSlots: BookingSlot[];
  openingTimes: OpeningTimes;
  facets: Facet[];
  floorplans: Floorplan[];
  closures: Closure[];
  discount?: Discount;
  discounts?: Discount[];
  organisation?: { name: string; largeIcon?: string; url?: string };
}

const fixOpeningTimes = (times: OpeningTimes): OpeningTimes =>
  weekdays
    .map(day =>
      times[`${day}Start`] &&
      times[`${day}End`] &&
      times[`${day}Start`] !== times[`${day}End`]
        ? {
            [`${day}Start`]: times[`${day}Start`],
            [`${day}End`]: times[`${day}End`],
          }
        : {}
    )
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

const fixLatLng = ({ lat, lng }: { lat: number; lng: number }) => ({
  lat: Number(lat),
  lng: Number(lng),
});

export const fixSpace = <S extends Space = Space>(space: S): S => ({
  ...space,
  openingTimes: fixOpeningTimes(space.openingTimes || {}),
});

export const fixVenue = <V extends Venue = Venue>({
  latLon: latLng,
  ...venue
}: V & { latLon?: { lat: number; lng: number } }): V =>
  ({
    ...(latLng?.lat && latLng.lng ? { latLng: fixLatLng(latLng) } : {}),
    ...venue,
    openingTimes: fixOpeningTimes(venue.openingTimes || {}),
    ...(venue.spaces ? { spaces: venue.spaces.map(fixSpace) } : {}),
  }) as V;

export const getVenue = (api: AxiosInstance, id: number, params: object = {}) =>
  api
    .get<Venue>(`/v1/venues/${id}`, { params })
    .then(({ data }) => fixVenue(data));

const useVenue = (venueId: number, includeSpaces: boolean = false) => {
  const [venue, setVenue] = useState<Venue | undefined>(undefined);

  const apiRequest = useApiRequest();

  const requestValid = !!venueId;

  const clearData = useCallback(async () => {
    setVenue(undefined);
  }, []);

  const fetchData = useCallback(async () => {
    setVenue(await getVenue(apiRequest, venueId, { includeSpaces }));
  }, [apiRequest, venueId, includeSpaces]);

  const { loading, error, refresh } = useAsync(
    requestValid ? fetchData : clearData,
    requestValid
  );

  return { data: venue, loading, error, refresh };
};

export default useVenue;
